const API_BASE = (import.meta.env.VITE_API_BASE as string | undefined)?.replace(/\/$/, "") ?? "";

export class ApiError extends Error {
  status: number;
  code?: string;
  details?: Record<string, string[]>;

  constructor(status: number, message: string, code?: string, details?: Record<string, string[]>) {
    super(message);
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

let csrfToken: string | null = null;

async function ensureCsrf(): Promise<string> {
  if (csrfToken) return csrfToken;
  const response = await fetch(`${API_BASE}/api/v1/auth/csrf`, {
    credentials: "include"
  });
  if (!response.ok) throw new ApiError(response.status, "Не удалось подготовить защищённый запрос.");
  const body = (await response.json()) as { token: string };
  csrfToken = body.token;
  return body.token;
}

export async function api<T>(
  path: string,
  options: RequestInit & { idempotencyKey?: string } = {}
): Promise<T> {
  const method = (options.method ?? "GET").toUpperCase();
  const headers = new Headers(options.headers);
  if (options.body && !(options.body instanceof FormData) && !headers.has("Content-Type")) {
    headers.set("Content-Type", "application/json");
  }
  if (!["GET", "HEAD", "OPTIONS"].includes(method)) {
    headers.set("X-XSRF-TOKEN", await ensureCsrf());
  }
  if (options.idempotencyKey) {
    headers.set("Idempotency-Key", options.idempotencyKey);
  }

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    method,
    headers,
    credentials: "include"
  });

  if (response.status === 204) return undefined as T;
  const contentType = response.headers.get("content-type") ?? "";
  const body = contentType.includes("json") ? await response.json() : await response.text();
  if (!response.ok) {
    const problem = typeof body === "object" && body ? (body as Record<string, unknown>) : {};
    const title =
      (problem.detail as string | undefined) ??
      (problem.title as string | undefined) ??
      (typeof body === "string" ? body : undefined) ??
      "Запрос не выполнен.";
    throw new ApiError(
      response.status,
      title,
      problem.code as string | undefined,
      problem.errors as Record<string, string[]> | undefined
    );
  }
  if (
    path === "/api/v1/auth/login" ||
    path === "/api/v1/auth/logout" ||
    path === "/api/v1/profile/deletion-confirm"
  ) {
    csrfToken = null;
  }
  return body as T;
}

export function newIdempotencyKey(prefix: string): string {
  return `${prefix}-${crypto.randomUUID()}`;
}

export function apiFileUrl(path: string): string {
  return `${API_BASE}${path}`;
}
